import React, { useState, useMemo, useEffect } from 'react';
import {
  Box, Button, Chip, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions,
  Grid, TextField, MenuItem, FormControlLabel, Switch,
  Card, CardContent, Typography, Divider
} from '@mui/material';
import { DataGrid } from '../common/datagrid/ResponsiveDataGrid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import * as eqpApi from '../../lib/api/eqp';
import { DOWNTIME_TYPES } from '../../containers/eqpmng/EqpMngContainer';

// ====================================================================
// 입력 폼 모달
// ====================================================================
const LogFormModal = ({ open, data, onClose, onSave, logTypeLabels = {}, logTypeColors = {} }) => {
  const isNew = !data;

  const formik = useFormik({
    initialValues: {
      logTyp:     data?.logTyp     || 'OPER',
      stDt:       data?.stDt       || '',
      edDt:       data?.edDt       || '',
      isDowntime: data?.isDowntime || 'N',
      cost:       data?.cost       || '',
      worker:     data?.worker     || '',
      rmk:        data?.rmk        || '',
    },
    validationSchema: Yup.object({
      logTyp: Yup.string().required('유형은 필수입니다.'),
      stDt:   Yup.string().required('시작일시는 필수입니다.'),
    }),
    enableReinitialize: true,
    onSubmit: (values) => {
      onSave(values, data);
      onClose();
    },
  });

  // 유형 변경 시 다운타임 자동 설정
  const handleLogTypChange = (e) => {
    const typ = e.target.value;
    formik.setFieldValue('logTyp', typ);
    formik.setFieldValue('isDowntime', DOWNTIME_TYPES.has(typ) ? 'Y' : 'N');
  };

  // 경과시간 미리보기
  const previewDuration = useMemo(() => {
    if (!formik.values.stDt || !formik.values.edDt) return null;
    try {
      const st = dayjs(formik.values.stDt);
      const ed = dayjs(formik.values.edDt);
      const mins = ed.diff(st, 'minute');
      if (mins < 0) return null;
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      return `${h}시간 ${m}분 (${(mins / 60).toFixed(2)}h)`;
    } catch { return null; }
  }, [formik.values.stDt, formik.values.edDt]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isNew ? '운영이력 등록' : '운영이력 수정'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Grid container spacing={2}>

            {/* 유형 */}
            <Grid item xs={12} sm={6}>
              <TextField
                select fullWidth label="분류 *" name="logTyp" size="small"
                value={formik.values.logTyp}
                onChange={handleLogTypChange}
                error={formik.touched.logTyp && Boolean(formik.errors.logTyp)}
              >
                {Object.entries(logTypeLabels).map(([v, l]) => (
                  <MenuItem key={v} value={v}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label={l} color={logTypeColors[v] || 'default'} size="small" />
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* 다운타임 여부 */}
            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formik.values.isDowntime === 'Y'}
                    onChange={(e) =>
                      formik.setFieldValue('isDowntime', e.target.checked ? 'Y' : 'N')
                    }
                    color="error"
                  />
                }
                label={
                  <Typography variant="body2" color={formik.values.isDowntime === 'Y' ? 'error' : 'text.secondary'}>
                    {formik.values.isDowntime === 'Y' ? '⚠ 다운타임' : '다운타임 아님'}
                  </Typography>
                }
              />
            </Grid>

            {/* 시작일시 */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="시작일시 *" name="stDt" size="small"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={formik.values.stDt}
                onChange={formik.handleChange}
                error={formik.touched.stDt && Boolean(formik.errors.stDt)}
                helperText={formik.touched.stDt && formik.errors.stDt}
              />
            </Grid>

            {/* 종료일시 */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="종료일시" name="edDt" size="small"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={formik.values.edDt}
                onChange={formik.handleChange}
                helperText={previewDuration ? `경과: ${previewDuration}` : '비워두면 진행중으로 저장'}
              />
            </Grid>

            {/* 작업자/업체 */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="작업자/업체" name="worker" size="small"
                value={formik.values.worker}
                onChange={formik.handleChange}
              />
            </Grid>

            {/* 비용 */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="비용 (원)" name="cost" size="small" type="number"
                value={formik.values.cost}
                onChange={formik.handleChange}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>

            {/* 비고 */}
            <Grid item xs={12}>
              <TextField
                fullWidth label="비고 / comment" name="rmk" size="small"
                multiline rows={2}
                value={formik.values.rmk}
                onChange={formik.handleChange}
              />
            </Grid>

          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button variant="contained" onClick={formik.handleSubmit}>
          {isNew ? '등록' : '수정'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ====================================================================
// 월별 요약 카드
// ====================================================================
const MonthlySummaryCard = ({ list, planHr }) => {
  const summary = useMemo(() => {
    const totalOper = list
      .filter(r => r.logTyp === 'OPER' && r.durationHr)
      .reduce((s, r) => s + parseFloat(r.durationHr || 0), 0);
    const totalDown = list
      .filter(r => r.isDowntime === 'Y' && r.durationHr)
      .reduce((s, r) => s + parseFloat(r.durationHr || 0), 0);
    const totalAll = list
      .filter(r => r.durationHr)
      .reduce((s, r) => s + parseFloat(r.durationHr || 0), 0);
    // 예상 가동시간이 있으면 그걸 분모로, 없으면 전체 로그 시간 합산
    const denominator = planHr > 0 ? planHr : totalAll;
    const operRate = denominator > 0 ? (totalOper / denominator * 100).toFixed(1) : null;
    const downtimeCnt = list.filter(r => r.isDowntime === 'Y').length;
    const totalCost = list.reduce((s, r) => s + (r.cost ? Number(r.cost) : 0), 0);
    return { totalOper, totalDown, operRate, downtimeCnt, totalCost };
  }, [list, planHr]);

  const items = [
    { label: '가동시간', value: `${summary.totalOper.toFixed(1)}h`, color: '#4caf50' },
    { label: '예상시간', value: planHr > 0 ? `${Number(planHr).toFixed(1)}h` : '-', color: '#1976d2' },
    { label: '다운타임', value: `${summary.totalDown.toFixed(1)}h`, color: '#f44336' },
    { label: '가동률',   value: summary.operRate ? `${summary.operRate}%` : '-', color: summary.operRate >= 80 ? '#4caf50' : '#ff9800' },
    { label: '고장건수', value: `${summary.downtimeCnt}건`, color: summary.downtimeCnt > 0 ? '#f44336' : '#999' },
    { label: '총 비용',  value: summary.totalCost > 0 ? `${summary.totalCost.toLocaleString()}원` : '-', color: '#555' },
  ];

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {items.map(({ label, value, color }) => (
            <Box key={label} sx={{ textAlign: 'center', minWidth: 80 }}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color }}>{value}</Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

// ====================================================================
// 메인 탭 컴포넌트
// ====================================================================
const EqpLogTab = ({ eqpUuid, list, onSave, onDelete, logTypeLabels = {}, logTypeColors = {} }) => {
  const [formOpen, setFormOpen]       = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [filterYm, setFilterYm]       = useState('');
  const [filterTyp, setFilterTyp]     = useState('');
  const [planHr, setPlanHr]           = useState(0);

  // filterYm 변경 시 해당 월 예상 가동시간 조회
  useEffect(() => {
    if (!eqpUuid || !filterYm || filterYm.length !== 6) { setPlanHr(0); return; }
    eqpApi.getPlanList({ eqpUuid, searchYear: filterYm.slice(0, 4) })
      .then(res => {
        const found = (res.data.list || []).find(p => p.planYm === filterYm);
        setPlanHr(found?.planHr || 0);
      })
      .catch(() => setPlanHr(0));
  }, [eqpUuid, filterYm]);

  // 필터링된 목록
  const filteredList = useMemo(() => {
    return list.filter(r => {
      const ymOk  = !filterYm  || (r.stDt && r.stDt.replace('-', '').slice(0, 6) === filterYm.replace('-', ''));
      const typOk = !filterTyp || r.logTyp === filterTyp;
      return ymOk && typOk;
    });
  }, [list, filterYm, filterTyp]);

  const columns = [
    {
      field: 'logTyp', headerName: '분류', width: 110,
      renderCell: ({ value }) => (
        <Chip
          label={logTypeLabels[value] || value}
          color={logTypeColors[value] || 'default'}
          size="small"
        />
      ),
    },
    { field: 'stDt',  headerName: '시작일시', width: 145 },
    {
      field: 'edDt', headerName: '종료일시', width: 145,
      renderCell: ({ value }) => value || <Chip label="진행중" color="warning" size="small" />,
    },
    {
      field: 'durationHr', headerName: '경과(h)', width: 90,
      renderCell: ({ value }) => value ? `${parseFloat(value).toFixed(2)}h` : '-',
    },
    {
      field: 'isDowntime', headerName: '다운타임', width: 85,
      renderCell: ({ value }) =>
        value === 'Y'
          ? <Chip label="다운" color="error" size="small" />
          : <Chip label="정상" color="default" size="small" variant="outlined" />,
    },
    { field: 'worker', headerName: '작업자/업체', width: 120 },
    {
      field: 'cost', headerName: '비용', width: 110,
      valueFormatter: (value) => value ? `${Number(value).toLocaleString()}원` : '-',
    },
    { field: 'rmk', headerName: '비고', flex: 1, minWidth: 120 },
    {
      field: 'actions', headerName: '', width: 80, sortable: false,
      renderCell: ({ row }) => (
        <Box>
          <Tooltip title="수정">
            <IconButton size="small"
              onClick={() => { setSelectedLog(row); setFormOpen(true); }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="삭제">
            <IconButton size="small" color="error"
              onClick={() => onDelete(row.logUuid)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      {/* 월별 요약 카드 */}
      <MonthlySummaryCard list={filteredList} planHr={planHr} />

      {/* 필터 + 등록 버튼 */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          label="년월" size="small" placeholder="YYYYMM"
          value={filterYm}
          onChange={(e) => setFilterYm(e.target.value)}
          sx={{ width: 120 }}
        />
        <TextField
          select label="분류" size="small"
          value={filterTyp}
          onChange={(e) => setFilterTyp(e.target.value)}
          sx={{ width: 130 }}
        >
          <MenuItem value="">전체</MenuItem>
          {Object.entries(logTypeLabels).map(([v, l]) => (
            <MenuItem key={v} value={v}>{l}</MenuItem>
          ))}
        </TextField>
        <Box sx={{ ml: 'auto' }}>
          <Button
            variant="outlined" size="small" startIcon={<AddIcon />}
            onClick={() => { setSelectedLog(null); setFormOpen(true); }}
          >
            이력 등록
          </Button>
        </Box>
      </Box>

      {/* DataGrid */}
      <DataGrid
        rows={filteredList}
        columns={columns}
        getRowId={(row) => row.logUuid}
        autoHeight
        disableColumnMenu
        pageSizeOptions={[20, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
        getRowClassName={({ row }) =>
          row.isDowntime === 'Y' ? 'downtime-row' : ''
        }
        sx={{
          '& .downtime-row': {
            backgroundColor: 'rgba(244,67,54,0.05)',
          },
        }}
      />

      <LogFormModal
        open={formOpen}
        data={selectedLog}
        onClose={() => { setFormOpen(false); setSelectedLog(null); }}
        onSave={onSave}
        logTypeLabels={logTypeLabels}
        logTypeColors={logTypeColors}
      />
    </Box>
  );
};

export default EqpLogTab;
